const m = require('minthril');
const { component } = require('../../component');

const graphWidth = 720;
const graphHeight = 180;
const graphPadding = { top: 12, right: 8, bottom: 28, left: 42 };
const periodOptions = [
  { label: 'Day', hours: 24 },
  { label: 'Week', hours: 7 * 24 },
  { label: 'Month', hours: 30 * 24 },
  { label: 'Year', hours: 365 * 24 }
];

function hourKey(date) {
  return date.toISOString().slice(0, 13);
}

function hourlyBuckets(hourlyUsage, periodHours) {
  const values = new Map(hourlyUsage.map(hour => [hour.hour, hour]));
  const currentHour = new Date();
  currentHour.setUTCMinutes(0, 0, 0);

  return Array.from({ length: periodHours }, (_, index) => {
    const date = new Date(currentHour);
    date.setUTCHours(date.getUTCHours() - (periodHours - index - 1));
    const hour = values.get(hourKey(date)) || {};

    return {
      ...hour,
      cacheHits: hour.cacheHits || 0,
      cacheMisses: hour.cacheMisses || 0,
      date,
      pageHits: hour.pageHits || 0
    };
  });
}

function dayLabel(date) {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
}

function formatHour(date) {
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    timeZoneName: 'short'
  });
}

function formatWindow(hours) {
  if (hours === 24) {
    return '1 day';
  }

  if (hours === 7 * 24) {
    return '7 days';
  }

  if (hours === 30 * 24) {
    return '1 month';
  }

  if (hours === 365 * 24) {
    return '1 year';
  }

  if (hours >= 24 && hours % 24 === 0) {
    return `${hours / 24} days`;
  }

  return `${hours} hours`;
}

function chartMaximum(value) {
  const magnitude = 10 ** Math.floor(Math.log10(value || 1));
  const normalisedValue = value / magnitude;
  const step = normalisedValue <= 1 ? 1 : normalisedValue <= 2 ? 2 : normalisedValue <= 5 ? 5 : 10;
  return step * magnitude;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

const HourlyUsageGraph = component((vnode, emit) => {
  let hoveredBucketIndex = null;
  let windowStart = 0;
  let windowEnd = null;
  let dragStart = null;
  let dragEnd = null;
  let previousPeriodHours = null;

  function setHoveredBucket(index) {
    hoveredBucketIndex = index;
    emit('redraw');
  }

  function bucketAtPointer(event, bucketCount) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const svgPosition = (event.clientX - bounds.left) / bounds.width * graphWidth;
    const chartWidth = graphWidth - graphPadding.left - graphPadding.right;

    if (svgPosition < graphPadding.left || svgPosition > graphWidth - graphPadding.right) {
      return null;
    }

    const position = clamp((svgPosition - graphPadding.left) / chartWidth, 0, 0.9999);
    const end = windowEnd || bucketCount;
    return windowStart + Math.floor(position * (end - windowStart));
  }

  function startDrag(event, bucketCount) {
    event.preventDefault();
    dragStart = bucketAtPointer(event, bucketCount);

    if (dragStart === null) {
      return;
    }

    dragEnd = dragStart;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    emit('redraw');
  }

  function updateDrag(event, bucketCount) {
    if (dragStart === null) {
      return;
    }

    event.preventDefault();
    const bucket = bucketAtPointer(event, bucketCount);

    if (bucket === null) {
      return;
    }

    dragEnd = bucket;
    emit('redraw');
  }

  function finishDrag(event, bucketCount) {
    if (dragStart === null) {
      return;
    }

    const selectionStart = Math.min(dragStart, dragEnd);
    const selectionEnd = Math.max(dragStart, dragEnd) + 1;
    dragStart = null;
    dragEnd = null;

    if (selectionEnd - selectionStart > 1) {
      windowStart = selectionStart;
      windowEnd = selectionEnd;
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    emit('redraw');
  }

  function zoomOut(bucketCount) {
    const end = windowEnd || bucketCount;
    const windowSize = end - windowStart;
    const expandedSize = Math.min(bucketCount, windowSize * 2);
    const centre = windowStart + windowSize / 2;
    windowStart = clamp(Math.round(centre - expandedSize / 2), 0, bucketCount - expandedSize);
    windowEnd = windowStart + expandedSize;
    emit('redraw');
  }

  function moveToNow(bucketCount) {
    const end = windowEnd || bucketCount;
    const windowSize = end - windowStart;
    windowEnd = bucketCount;
    windowStart = Math.max(0, bucketCount - windowSize);
    emit('redraw');
  }

  function selectPeriod(periodHours, onPeriodChange) {
    if (periodHours === previousPeriodHours) {
      windowStart = 0;
      windowEnd = null;
      emit('redraw');
      return;
    }

    onPeriodChange(periodHours);
  }

  return {
    view: ({ attrs: {
      hourlyUsage,
      periodHours,
      onPeriodChange,
      metric = 'pageHits',
      title = 'Traffic',
      subtitle = 'Hourly page hits',
      metricLabel = 'Page hits',
      formatValue = value => value.toLocaleString(),
      formatAxisValue = formatValue,
      variant = ''
    } }) => {
      if (previousPeriodHours !== periodHours) {
        windowStart = 0;
        windowEnd = null;
        previousPeriodHours = periodHours;
      }

      const buckets = hourlyBuckets(hourlyUsage, periodHours);
      const end = windowEnd || buckets.length;
      const visibleBuckets = buckets.slice(windowStart, end);
      const valueForBucket = bucket => Number(bucket[metric] || 0);
      const highestValue = chartMaximum(Math.max(1, ...visibleBuckets.map(valueForBucket)));
      const chartWidth = graphWidth - graphPadding.left - graphPadding.right;
      const chartHeight = graphHeight - graphPadding.top - graphPadding.bottom;
      const barWidth = chartWidth / visibleBuckets.length;
      const labelEvery = Math.max(1, Math.ceil(visibleBuckets.length / 7));
      const hoveredBucket = hoveredBucketIndex === null ? null : visibleBuckets[hoveredBucketIndex];
      const isZoomed = windowStart !== 0 || end !== buckets.length;

      if (hoveredBucketIndex !== null && !hoveredBucket) {
        hoveredBucketIndex = null;
      }

      return m(`section.hourly-usage-graph${variant ? ` hourly-usage-graph--${variant}` : ''}`,
          m('div.hourly-usage-graph__heading',
          m('div',
            m('h3', `${title} · ${formatWindow(visibleBuckets.length)}`),
            m('p', subtitle)
          ),
          m('div.hourly-usage-graph__legend',
            m('span', m('i.hourly-usage-graph__key'), metricLabel),
            m('strong', `${formatValue(visibleBuckets.reduce((total, bucket) => total + valueForBucket(bucket), 0))} total`),
            m('button.hourly-usage-graph__zoom', {
              type: 'button',
              class: 'button button--quiet hourly-usage-graph__zoom',
              disabled: !isZoomed,
              onclick: () => zoomOut(buckets.length)
            }, 'Zoom out'),
            m('button.hourly-usage-graph__zoom', {
              type: 'button',
              class: 'button button--quiet hourly-usage-graph__zoom',
              disabled: end >= buckets.length,
              onclick: () => moveToNow(buckets.length)
            }, 'Now')
          ),
          onPeriodChange ? m('div.hourly-usage-graph__periods',
            periodOptions.map(option => m('button', {
              type: 'button',
              class: `button button--quiet hourly-usage-graph__period${
                option.hours === periodHours ? ' hourly-usage-graph__period--current' : ''}`,
              onclick: () => selectPeriod(option.hours, onPeriodChange)
            }, option.label))
          ) : null
        ),
        m('div.hourly-usage-graph__plot', [
          m('svg.hourly-usage-graph__chart', {
            viewBox: `0 0 ${graphWidth} ${graphHeight}`,
            role: 'img',
            'aria-label': subtitle,
            onpointerdown: event => startDrag(event, buckets.length),
            onpointermove: event => updateDrag(event, buckets.length),
            onpointerup: event => finishDrag(event, buckets.length),
            onpointercancel: event => finishDrag(event, buckets.length)
          }, [
            [0, 0.25, 0.5, 0.75, 1].map(fraction => {
              const y = graphPadding.top + chartHeight * (1 - fraction);
              return [
                m('line', {
                  x1: graphPadding.left,
                  x2: graphWidth - graphPadding.right,
                  y1: y,
                  y2: y,
                  class: 'hourly-usage-graph__gridline'
                }),
                m('text', {
                  x: graphPadding.left - 6,
                  y: y + 4,
                  class: 'hourly-usage-graph__value',
                  'text-anchor': 'end'
                }, formatAxisValue(Math.round(highestValue * fraction)))
              ];
            }),
            visibleBuckets.map((bucket, index) => {
              const value = valueForBucket(bucket);
              const barHeight = value / highestValue * chartHeight;
              const isHovered = hoveredBucketIndex === index;
              return m('rect', {
                x: graphPadding.left + index * barWidth + 0.5,
                y: graphPadding.top + chartHeight - barHeight,
                width: Math.max(1, barWidth - 1),
                height: Math.max(1, barHeight),
                tabindex: 0,
                role: 'graphics-symbol',
                'aria-label': `${formatHour(bucket.date)}: ${formatValue(value)} ${metricLabel.toLowerCase()}`,
                class: `hourly-usage-graph__bar${isHovered ? ' hourly-usage-graph__bar--hovered' : ''}`,
                onmouseenter: () => setHoveredBucket(index),
                onmouseleave: () => setHoveredBucket(null),
                onfocus: () => setHoveredBucket(index),
                onblur: () => setHoveredBucket(null)
              }, m('title', `${formatHour(bucket.date)}: ${formatValue(value)} ${metricLabel.toLowerCase()}`));
            }),
            dragStart !== null ? m('rect.hourly-usage-graph__selection', {
              x: graphPadding.left + (Math.min(dragStart, dragEnd) - windowStart) / visibleBuckets.length * chartWidth,
              y: graphPadding.top,
              width: (Math.abs(dragEnd - dragStart) + 1) / visibleBuckets.length * chartWidth,
              height: chartHeight
            }) : null,
            visibleBuckets.map((bucket, index) => index % labelEvery === 0 ? m('text', {
              x: graphPadding.left + index * barWidth,
              y: graphHeight - 7,
              class: 'hourly-usage-graph__label'
            }, dayLabel(bucket.date)) : null)
          ]),
          hoveredBucket ? m('div.hourly-usage-graph__tooltip', {
            style: {
              left: `${graphPadding.left / graphWidth * 100 + (hoveredBucketIndex + 0.5) / buckets.length * (chartWidth / graphWidth * 100)}%`
            }
          }, [
            m('strong', `${formatValue(valueForBucket(hoveredBucket))} ${metricLabel.toLowerCase()}`),
            m('span', formatHour(hoveredBucket.date))
          ]) : null
        ])
      );
    }
  };
});

module.exports = { HourlyUsageGraph };
